import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { JobsListBlock } from 'stile-shared/src/components/2020/blocks/JobsListBlock';
import { MediaBlock } from 'stile-shared/src/components/2020/blocks/MediaBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { P } from 'stile-shared/src/components/2020/common/Primitives';
import { ImpactUL } from 'stile-shared/src/components/2024/common/AltUnderline/AltUnderline';
import { PageContextType } from 'stile-shared/src/types/data';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export { Head } from 'templates/2020/Head';

function JoinTheTeamPage(props: PageProps<Record<string, unknown>, PageContextType>) {
  const { hero } = getPageData(props.location.pathname);

  const knownJobIds = Object.keys(props.pageContext.jobMeta);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        media={
          <MediaBlock as="div" layout="step-repeat-five-left">
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/join-the-team/comp_join_team_01.jpg"
              alt="A support staff member wearing a call headset and smiling"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/join-the-team/comp_join_team_02.jpg"
              alt="The mural in Stile HQ's foyer. It is full of cartoon-style artwork in black and white, with green and pink accents"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/join-the-team/comp_join_team_03.jpg"
              alt="An engineer does focused work in a booth"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/join-the-team/comp_join_team_04.jpg"
              alt="A software engineer at a workstation with a laptop and a wide external monitor"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/join-the-team/comp_join_team_05.jpg"
              alt="A closeup of someone's desk. Headphones and a cupcake-shaped squeeze toy are in focus"
            />
          </MediaBlock>
        }
      />

      <TextBlock>
        <h2>Life at Stile</h2>
        <h3>
          Where <ImpactUL>impact</ImpactUL> meets joy
        </h3>
        <P>
          We believe education can, and should, be better, and we’re here to make that happen. This
          is a place for those who care deeply about quality, who thrive in a nimble environment
          where decisions are fast, and impact is tangible.
        </P>

        <P>
          It’s for those who find joy in working alongside brilliant, passionate people, and who
          know that doing great work and having fun aren’t mutually exclusive.
        </P>

        <P>
          Most of all, Stile is powered by a community of curious, ambitious, diverse humans who
          choose to bring their talents to our mission - and we’re committed to looking after them
          every step of the way.
        </P>
      </TextBlock>

      <TeaserBlock
        variant="dark"
        contentMax="450px"
        title="Meet the Stile team"
        text="We asked the team how they would describe a Stilist. Here is what they had to say."
        video={{
          url: 'https://www.youtube.com/embed/tehcTUqr9t4',
          text: 'Meet the Stile team',
          coverImage: (
            <StaticImage
              src="../../../../stile-shared/assets/images/join-the-team/culture_video_cover.jpg"
              alt="Several Stile staff members sitting in a relaxed meeting area. One is explaining something and the others are listening attentively"
            />
          ),
        }}
      />

      <TextBlock title="Impact with purpose">
        <P>
          You’re not just joining a team, you’re joining a mission. At Stile, you’ll drive real
          change in science education, empowering schools to think deeper, learn better, and reach
          higher. If you want your work to matter, this is the place.
        </P>
      </TextBlock>

      <TextBlock title="Together when it counts, flexible when you need">
        <P>
          We believe the best ideas spark when we’re together, but we also value balance and know
          that life doesn’t run on a 9–5 schedule. We value results, not hours clocked.
        </P>
        <Ul>
          <li>
            <strong>Flexible working:</strong> Whether it’s school pick-up or an important
            appointment, you’ll have the flexibility to step out when you need to.
          </li>
          <li>
            <strong>Five weeks of annual leave</strong>: Take four weeks of leave in a year and get
            one more for free.
          </li>
          <li>
            <strong>Remote road trips</strong>: Take off on a working holiday for a few weeks.
            Dial-in from the beach, the snow, whatever suits.
          </li>
          <li>
            <strong>Sabbaticals</strong>: After three years working at Stile, you can do a 3-month
            unpaid stint doing whatever you want!
          </li>
          <li>
            <strong>Wellbeing support</strong>: Six free, confidential counselling sessions every
            year.
          </li>
          <li>
            <strong>A purpose-built HQ in Melbourne’s CBD</strong>: Designed for collaboration with
            team meeting spaces plus your very own desk (no hotdesks here).
          </li>
          <li>
            <strong>A kitchen worth bragging about</strong>: Stocked with specialty coffee, craft
            brews, local wines, and all the gear for cook-ups or the perfect toastie.
          </li>
        </Ul>
      </TextBlock>

      <TextBlock title="Grow, Learn, Lead">
        <P>
          Every Stilist gets $1,500 each year to invest in personal development, plus access to
          coaching and career planning, because your ambitions matter as much to us as they do to
          you.
        </P>

        <P>
          We make sure every voice is heard, with weekly check-ins, an open-door policy, and “ask me
          anything” sessions with our CEO.
        </P>

        <P>
          And because great leadership matters, we keep manager teams small (no more than eight
          people) so you get the attention and support you deserve. Your manager will know your
          strengths, your goals, and your challenges—and will meet with you every week to help you
          grow and succeed.
        </P>
      </TextBlock>

      <TextBlock title="We need your help">
        <P>Interested in joining the team? We’d love to hear from you.</P>

        <P>
          Before you apply, make sure you read our{' '}
          <Link url="/who-we-are/effective-stilists/">Team Values</Link> of highly effective
          Stilists. You may also want to take a look at our{' '}
          <Link url="/who-we-are/hiring-process/">Hiring Process</Link> for joining the team.
        </P>

        <P>
          If you’re a software engineer and have an interest in what we do, or the technical
          problems we’re solving, be sure to check out our{' '}
          <Link url="/who-we-are/engineering-at-stile/">Engineering at Stile</Link> page too.
        </P>

        <P>
          Want a closer look at how we work? Check out{' '}
          <Link url="https://inside.stileeducation.com/">Inside Stile</Link>, our guide to life
          here.
        </P>

        <P>
          If you don’t see yourself on the list below, consider{' '}
          <Link url="/who-we-are/contact/">sending us an email</Link> anyway. Maybe you’ll blow our
          socks off and we’ll make a position for you. What’s the worst that could happen?
        </P>
      </TextBlock>

      <JobsListBlock knownJobIds={knownJobIds} />

      <NextPageBlock path="/who-we-are/gender-diversity-in-stem/" />

      <CtaBlock />
    </PageLayout>
  );
}

const Ul = styled.ul`
  list-style-type: disc;
  padding-left: 40px;
  margin-top: 0;
  margin-bottom: 0;
  padding-right: 40px;

  li {
    margin-bottom: 8px;
    line-height: 1.5;
  }
`;

export default JoinTheTeamPage;
